import { Leaf, Repeat, Cpu, Compass, RotateCw, MoveDown, MoveRight } from "lucide-react";
import { MaskedLine, Reveal, SectionNumber } from "./Reveal";

const PILLARS = [
    {
        icon: Leaf,
        title: "Environment",
        desc: "Sustainability strategy, regulatory compliance and circular system design.",
    },
    {
        icon: Repeat,
        title: "Trading",
        desc: "International sourcing and commercialization of material flows, end to end.",
    },
    {
        icon: Cpu,
        title: "Technology",
        desc: "AI, data and automation embedded across every operation we run.",
    },
    {
        icon: Compass,
        title: "Consultancy",
        desc: "Project development and management — from concept to daily operations.",
    },
];

const WasteFlowCard = () => (
    <div
        data-testid="about-waste-flow"
        className="flex h-64 flex-col justify-between rounded-2xl border border-[#DCE5D4] bg-white p-6 md:h-80 md:p-7"
    >
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#598B3B]">
            Waste Material Recovery — Circular Economy Flow
        </p>
        <div className="flex flex-col gap-2">
            {["Hazardous Waste", "Non-Hazardous Waste"].map((w) => (
                <span
                    key={w}
                    className="w-max rounded-md border border-[#DCE5D4] bg-[#F7F9F5] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#45543F]"
                >
                    {w}
                </span>
            ))}
        </div>
        <div className="flex items-center gap-3">
            <MoveDown size={16} className="text-[#598B3B] md:hidden" strokeWidth={1.8} />
            <MoveRight size={16} className="hidden text-[#598B3B] md:block" strokeWidth={1.8} />
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#598B3B]/10 text-[#598B3B]">
                <RotateCw size={19} strokeWidth={1.6} className="animate-spin-slower" />
            </span>
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#5C6B57]">
                Recovery & Treatment
            </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
            {["Recovered Materials", "Safe Treatment", "Energy Recovery"].map((o) => (
                <span
                    key={o}
                    className="rounded-md bg-[#7CBF52]/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#4a7530]"
                >
                    {o}
                </span>
            ))}
        </div>
    </div>
);

export const Pillars = () => (
    <section
        id="about"
        data-testid="pillars-section"
        className="relative py-28 md:py-40"
    >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <SectionNumber n="01" label="Who We Are" />
            <div className="grid gap-14 lg:grid-cols-[1.3fr_1fr] lg:gap-24">
                <h2 className="font-display text-5xl font-light leading-[1.06] tracking-tighter text-[#0E1112] sm:text-6xl lg:text-7xl">
                    <MaskedLine>The strength of a system</MaskedLine>
                    <MaskedLine delay={0.15} className="text-[#C8102E]">
                        is how its parts connect.
                    </MaskedLine>
                </h2>
                <div className="flex flex-col justify-end">
                    <Reveal delay={0.2}>
                        <p className="max-w-md text-sm leading-relaxed text-[#45543F] md:text-base">
                            NOMAS is a UAE-based integrated services and solutions
                            provider. Six solutions, one accountable partner —
                            every output becomes the next input.
                        </p>
                    </Reveal>
                </div>
            </div>

            <div className="mt-20 grid gap-px overflow-hidden rounded-2xl border border-[#DCE5D4] bg-[#DCE5D4] sm:grid-cols-2 lg:grid-cols-4">
                {PILLARS.map((p, i) => (
                    <Reveal key={p.title} delay={i * 0.08} className="h-full">
                        <div
                            data-testid={`pillar-${p.title.toLowerCase()}`}
                            className="group flex h-full flex-col bg-white p-8 transition-[background-color] duration-400 hover:bg-[#F2F7EC] md:p-9"
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#598B3B]/10 text-[#598B3B] transition-[background-color,color] duration-400 group-hover:bg-[#598B3B] group-hover:text-white">
                                <p.icon size={20} strokeWidth={1.6} />
                            </span>
                            <h3 className="font-display mt-7 text-2xl font-medium tracking-tight text-[#0E1112]">
                                {p.title}
                            </h3>
                            <p className="mt-3 text-[13px] leading-relaxed text-[#5C6B57]">
                                {p.desc}
                            </p>
                            <span className="font-display mt-auto pt-6 text-xs tracking-[0.3em] text-[#B9C9AE] transition-colors duration-400 group-hover:text-[#598B3B]">
                                0{i + 1}
                            </span>
                        </div>
                    </Reveal>
                ))}
            </div>

            <div className="mt-16 grid gap-5 md:grid-cols-3">
                <Reveal>
                    <figure
                        data-testid="about-image-uae"
                        className="group relative h-64 overflow-hidden rounded-2xl md:h-80"
                    >
                        <img
                            src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1200&auto=format&fit=crop"
                            alt="Dubai skyline — integrated facility management across the UAE"
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1112]/55 via-transparent to-transparent" />
                        <figcaption className="glass absolute bottom-4 left-4 rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0E1112]">
                            Integrated FM
                        </figcaption>
                    </figure>
                </Reveal>
                <Reveal delay={0.1}>
                    <figure
                        data-testid="about-image-facility"
                        className="group relative h-64 overflow-hidden rounded-2xl md:h-80"
                    >
                        <img
                            src="/assets/resource-recovery.jpg"
                            alt="NOMAS material recovery facility — sorting lines and recovered materials"
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1112]/55 via-transparent to-transparent" />
                        <figcaption className="glass absolute bottom-4 left-4 rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0E1112]">
                            Material Recovery Operations
                        </figcaption>
                    </figure>
                </Reveal>
                <Reveal delay={0.2} className="h-full">
                    <WasteFlowCard />
                </Reveal>
            </div>
        </div>
    </section>
);
